import { useCallback, useEffect } from 'react';
import {
  downloadFailedNoFileNotification,
  downloadFailedNotification,
} from '../../../components/notifications';
import { WixmpErrors } from '../../../constants/WixmpErrors';
import {
  FedopsInteractions,
  fedopsLogger,
} from '../../../services/fedopsLogger';
import { sentryClient } from '../../../services/sentryClient';
import { downloadVideo } from '../../downloadVideo';
import { useNotificatorConsumer } from '../useNotificator';
import { useWixmpGetDownloadLink } from '../usePrivateWixmp/usePrivateWixmp';
import { IUseDownloadAction } from './actions.types';

export const useDownloadAction = ({ mediaToken, file }: IUseDownloadAction) => {
  const { notify } = useNotificatorConsumer();

  const {
    isLoading,
    initRequest,
    error,
    wixmpResponse,
  } = useWixmpGetDownloadLink({
    mediaToken,
    file,
  });

  const downloadFileAction = useCallback(() => {
    fedopsLogger.interactionStarted(FedopsInteractions.Download);

    void initRequest(file.id).then(() => {
      fedopsLogger.interactionEnded(FedopsInteractions.Download);
    });
  }, [initRequest, file]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (error) {
      sentryClient.captureException(error);

      if (
        error.message &&
        error.message.includes(WixmpErrors.ResourceNotExists)
      ) {
        notify(downloadFailedNoFileNotification({ fileId: file.id }));

        return;
      }
      notify(
        downloadFailedNotification({
          onTryAgain: downloadFileAction,
          fileId: file.id,
        }),
      );

      return;
    }

    if (wixmpResponse) {
      try {
        if (wixmpResponse.data && wixmpResponse.data.urlPath) {
          downloadVideo(wixmpResponse.data.urlPath);
        } else {
          throw Error(`Can't parse download link for fileId: ${file.id}`);
        }
      } catch (error) {
        sentryClient.captureException(error);

        notify(
          downloadFailedNotification({
            onTryAgain: downloadFileAction,
            fileId: file.id,
          }),
        );
      }
    }
  }, [isLoading, error, wixmpResponse, notify, file, mediaToken]);

  return { downloadFileAction, isLoading };
};
